/** @jsxImportSource theme-ui */
import { Formik, useField } from 'formik';
import { values as getValues } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { Grid } from 'semantic-ui-react';

import { MaxTimeframeStartDateType, MinTimeframeStartDate } from 'utils/constants';
import { getMomentAgo } from 'utils/dateTimeHelpers';
import { nameof } from 'utils/typeHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import BdFormLabel from 'components/SidebarView/SettingsView/_common/BdFormLabel';
import {
  FormikSemanticInput,
  FormikSemanticInputRefAttributes,
} from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticInput';
import { SemanticFormikProps } from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticProps';

type BdDatePickerProps = { autoFocusMonth?: boolean };
export const BdDatePicker: React.FC<SemanticFormikProps & BdDatePickerProps> = ({
  name,
  autoFocusMonth,
}) => {
  const [field, , helpers] = useField(name);
  const { value } = field;
  const { setValue, setError } = helpers;
  const [overAllError, setoverAllError] = useState(false);

  const monthRef = React.useRef<FormikSemanticInputRefAttributes>(null);
  const dayRef = React.useRef<FormikSemanticInputRefAttributes>(null);
  const yearRef = React.useRef<FormikSemanticInputRefAttributes>(null);

  const monthRegex = new RegExp(`^(0?[1-9]|1[012])$`);
  const dayRegex = new RegExp(`^([0-2]?[0-9]|3[01]|[1-31])$`);
  const yearRegex = new RegExp(`^[0-9][0-9][0-9][0-9]$`);

  type CustomDateComponentsFM = {
    month: string;
    day: string;
    year: string;
  };

  const monthKeyUp = e => {
    if (e.key === 'Enter') {
      dayRef.current.focusAndSelectAll();
    }
  };

  const dayKeyUp = e => {
    if (e.key === 'Enter') {
      yearRef.current.focusAndSelectAll();
    }
  };

  const acceptDigitsOnly = e => {
    if (e.which < 48 || e.which > 57) {
      e.preventDefault();
    }
  };

  return (
    <Formik<CustomDateComponentsFM>
      validateOnBlur
      validateOnChange={false}
      enableReinitialize
      initialValues={{
        month: value ? moment(value).format('MM') : '', // value as Date
        day: value ? moment(value).format('DD') : '',
        year: value ? moment(value).format('YYYY') : '',
      }}
      validate={values => {
        const errors: any = {};
        if (values.month && !monthRegex.test(values.month)) {
          errors.month = 'Please specify a valid month';
        }
        if (values.day && !dayRegex.test(values.day)) {
          errors.day = 'Please specify a valid day';
        }
        if (values.year && values.year.length < 4 && !yearRegex.test(values.year)) {
          errors.year = 'Please specify a valid year';
        }
        if (
          (!values.day || !!errors.day || !values.month || !!errors.month) &&
          values.year.length === 4 &&
          +values.year < 2019
        ) {
          errors.year = 'Please specify a date on or after Jan 1, 2019';
        }
        if (
          (!values.day || !!errors.day || !values.month || !!errors.month) &&
          values.year &&
          +values.year > getMomentAgo('start-of-today').year()
        ) {
          errors.year = 'Please specify a date at least 1 week ago from today';
        }
        if (Object.keys(errors).length) {
          setError(getValues(errors).join('. '));
          setoverAllError(false);
          return errors;
        }
        if (values.day === '' || values.month === '' || values.year === '') {
          setValue(null);
          return null;
        }

        // validate entire date string -----------------------------------
        const parsedMoment = moment(
          `${values.month.padStart(2, '0')}-${values.day.padStart(2, '0')}-${values.year.padStart(
            4,
            '0'
          )}`,
          'MM-DD-YYYY',
          true
        );

        if (!parsedMoment.isValid()) {
          setoverAllError(true);
          setError('Please specify a valid date');
          return null;
        }

        // validate result date to be within range -----------------------------------
        if (
          parsedMoment &&
          parsedMoment.toDate() > getMomentAgo(MaxTimeframeStartDateType).toDate()
        ) {
          setoverAllError(true);
          setError('Specify a date at least 1 week ago from today');
          return null;
        }
        if (parsedMoment && parsedMoment.toDate() < moment(MinTimeframeStartDate).toDate()) {
          setoverAllError(true);
          setError('Specify a date on or after Jan 1, 2019');
          return null;
        }

        setoverAllError(false);
        setValue(parsedMoment.toDate());
        return null;
      }}
      onSubmit={null}
    >
      {({ values, setValues }) => (
        <>
          <Grid columns={3} className="no__margin">
            <Grid.Row sx={{ py: '0 !important', mt: '24px', mb: '8px' }}>
              <Grid.Column width="5" className="no__margin">
                <BdFormLabel marginTop="0">Month</BdFormLabel>
                <FlexGroup suffix={<BdIcon name="icon-forward-slash" />} gutter="0">
                  <FormikSemanticInput
                    ref={monthRef}
                    name={nameof<CustomDateComponentsFM>('month')}
                    error={overAllError}
                    placeholder="MM"
                    autoFocus={autoFocusMonth}
                    id="month"
                    onKeyUp={e => monthKeyUp(e)}
                    onKeyPress={e => acceptDigitsOnly(e)}
                    className="timeframe__input"
                    maxLength={2}
                    onValueChange={newVal => {
                      if (newVal.length === 2) {
                        setTimeout(() => dayRef.current.focusAndSelectAll());
                      }
                    }}
                    onBlur={() => {
                      if (values.month.length === 1) {
                        const newValues = {
                          ...values,
                          month:
                            values.month.length === 1
                              ? values.month.padStart(2, '0')
                              : values.month,
                        };
                        setValues(newValues);
                      }
                    }}
                  />
                </FlexGroup>
              </Grid.Column>
              <Grid.Column width="5" className="no__margin">
                <BdFormLabel marginTop="0">Day</BdFormLabel>
                <FlexGroup suffix={<BdIcon name="icon-forward-slash" />} gutter="0">
                  <FormikSemanticInput
                    ref={dayRef}
                    name={nameof<CustomDateComponentsFM>('day')}
                    error={overAllError}
                    placeholder="DD"
                    onKeyUp={e => dayKeyUp(e)}
                    onKeyPress={e => acceptDigitsOnly(e)}
                    className="timeframe__input"
                    maxLength={2}
                    onValueChange={newVal => {
                      if (newVal.length === 2) {
                        setTimeout(() => yearRef.current.focusAndSelectAll());
                      }
                    }}
                    onBlur={() => {
                      if (values.day.length === 1) {
                        const newValues = {
                          ...values,
                          day: values.day.length === 1 ? values.day.padStart(2, '0') : values.day,
                        };
                        setValues(newValues);
                      }
                    }}
                  />
                </FlexGroup>
              </Grid.Column>
              <Grid.Column width="6" className="no__margin">
                <BdFormLabel marginTop="0">Year</BdFormLabel>
                <FormikSemanticInput
                  ref={yearRef}
                  name={nameof<CustomDateComponentsFM>('year')}
                  error={overAllError}
                  placeholder="YYYY"
                  onKeyPress={e => acceptDigitsOnly(e)}
                  className="timeframe__input"
                  maxLength={4}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      )}
    </Formik>
  );
};

export default BdDatePicker;
